// Initial fresh content loading utility

// Clear browser caches for initial load
export const clearInitialCaches = async () => {
  try {
    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }

    // Clear sessionStorage for fresh start
    sessionStorage.clear();

    console.log('Initial caches cleared for fresh content');
  } catch (error) {
    console.error('Error clearing initial caches:', error);
  }
};

// Force initial page reload for fresh content
export const forceInitialReload = () => {
  // Clear caches first
  clearInitialCaches();
  
  // Force reload without cache
  window.location.reload(true);
};

// Add timestamp for initial fresh content
export const addInitialTimestamp = (url) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_initial=${Date.now()}`;
};

// Check for new version and auto-reload if needed
export const checkVersionAndReload = async () => {
  try {
    // Get stored version from localStorage
    const storedVersion = localStorage.getItem('app_version');
    const storedBuildId = localStorage.getItem('app_buildId');
    
    // Fetch current version from server (with cache busting)
    const versionUrl = `/version.json?t=${Date.now()}`;
    const response = await fetch(versionUrl);
    
    if (!response.ok) {
      console.warn('Could not fetch version.json, skipping version check');
      return;
    }
    
    const versionData = await response.json();
    // Normalize to a string. localStorage always returns strings, and version.json
    // may store buildId as a number (e.g. 1766741871). Comparing a stored string
    // against a numeric buildId with !== is ALWAYS true, which causes an endless
    // reload loop. Coercing both sides to String keeps the comparison type-safe.
    const currentBuildId = String(versionData.buildId);
    const currentVersion = String(versionData.version);
    
    // If no stored version, save current and continue
    if (!storedVersion || !storedBuildId) {
      localStorage.setItem('app_version', currentVersion);
      localStorage.setItem('app_buildId', currentBuildId);
      console.log('Initial version saved:', currentBuildId);
      return;
    }
    
    // If version changed, force reload
    if (storedBuildId !== currentBuildId) {
      console.log('🔄 New version detected!');
      console.log('Old build:', storedBuildId);
      console.log('New build:', currentBuildId);
      console.log('🔄 Clearing cache and reloading...');
      
      // Clear all caches
      await clearInitialCaches();

      // Clear localStorage except the auth token, then persist the NEW version.
      // Note: the version keys must be written AFTER localStorage.clear(),
      // otherwise clear() would wipe them and the next load would detect the
      // "change" again, causing a reload loop.
      const token = localStorage.getItem('token');
      localStorage.clear();
      if (token) {
        localStorage.setItem('token', token);
      }
      localStorage.setItem('app_version', currentVersion);
      localStorage.setItem('app_buildId', currentBuildId);

      // Force hard reload
      window.location.reload(true);
    } else {
      console.log('✅ App is up to date (build:', currentBuildId, ')');
    }
  } catch (error) {
    console.error('Error checking version:', error);
    // Don't block app if version check fails
  }
};

// Initialize fresh content loading on app startup
export const initFreshContent = () => {
  // Clear caches on app start for fresh content
  clearInitialCaches();
  
  console.log('Fresh content loading initialized');
}; 